import type { AiPreference } from "./connect-ai.js";

export const DEFAULT_AGENT_NAME = "Agent Me";

export type PublicAiMode = AiPreference;
export type RuntimeInferenceMode = "managed" | "subscription" | "byo_key" | "local_fallback";

export interface ConsumerInferenceStatusLike {
  mode: RuntimeInferenceMode;
  provider: string;
  model: string;
  isFallback: boolean;
  reason: string;
  qualityTier: "enhanced" | "standard" | "degraded";
}

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Claude",
  "azure-openai": "Azure OpenAI",
  google: "Gemini",
  local: "Built-in backup",
  openai: "OpenAI",
};

export function normalizeAgentName(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 48);
}

export function hasCustomAgentName(value: string | null | undefined): boolean {
  const normalized = normalizeAgentName(value);
  return normalized.length > 0 && normalized.toLowerCase() !== DEFAULT_AGENT_NAME.toLowerCase();
}

export function resolveAgentDisplayName(value: string | null | undefined): string {
  return normalizeAgentName(value) || DEFAULT_AGENT_NAME;
}

export function resolveAgentReference(value: string | null | undefined): string {
  return hasCustomAgentName(value) ? resolveAgentDisplayName(value) : "your agent";
}

export function resolveAgentLabel(value: string | null | undefined): string {
  return hasCustomAgentName(value) ? resolveAgentDisplayName(value) : "Agent";
}

export function resolveAgentHeading(value: string | null | undefined): string {
  return hasCustomAgentName(value) ? resolveAgentDisplayName(value) : "Your agent";
}

export function resolveAgentPossessive(value: string | null | undefined): string {
  const reference = resolveAgentReference(value);
  return reference === "your agent" ? "your agent's" : `${reference}'s`;
}

export function getPublicAiModeLabel(mode: PublicAiMode): string {
  switch (mode) {
    case "byo_subscription":
      return "Your subscription";
    case "byo_api":
      return "Your API key";
    default:
      return "Already ready";
  }
}

export function getRuntimeInferenceModeLabel(mode: RuntimeInferenceMode): string {
  switch (mode) {
    case "subscription":
      return "Using your subscription";
    case "byo_key":
      return "Using your API key";
    case "local_fallback":
      return "Limited right now";
    default:
      return "Ready to chat";
  }
}

export function getConsumerQualityLabel(
  qualityTier: ConsumerInferenceStatusLike["qualityTier"],
): string {
  switch (qualityTier) {
    case "enhanced":
      return "Full speed";
    case "degraded":
      return "Limited";
    default:
      return "Standard";
  }
}

export function getProviderLabel(provider: string | null | undefined): string {
  const normalized = String(provider ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Unknown";
  return PROVIDER_LABELS[normalized] ?? normalized.replace(/[-_]+/g, " ");
}

export function getConsumerInferenceReason(
  status: Pick<ConsumerInferenceStatusLike, "mode" | "isFallback">,
  options?: { requiresConnection?: boolean; platformIncluded?: boolean },
): string {
  if (status.mode === "local_fallback" || status.isFallback) {
    if (options?.requiresConnection) {
      return "This workspace is still limited until you connect your own subscription or API key.";
    }
    return "Things are a bit limited right now, but your agent can still help with what is already in your workspace.";
  }
  if (status.mode === "subscription") {
    if (options?.platformIncluded) {
      return "Your Agent Me plan includes platform-managed inference for this workspace.";
    }
    return "Your connected subscription is being used for this workspace.";
  }
  if (status.mode === "byo_key") {
    return "Your connected API key is being used for this workspace.";
  }
  if (options?.requiresConnection) {
    return "Connect your own subscription or API key to unlock the full experience.";
  }
  return "Your workspace is ready. Add your own subscription or API key any time if you want that used first.";
}
